import { useQuery } from "@apollo/client";
import { Table, useModal } from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import _ from "lodash";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ActionButtons } from "./action-buttons";
import { GET_VULNERABILITIES_SEVERITY_UPDATE_REQUESTS } from "./queries";
import { getColumns } from "./utils";

import { HandleSeverityRequestsForm } from "features/vulnerabilities/severity-request-form";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { VulnerabilityModal } from "features/vulnerabilities/vulnerability-modal";
import { useAudit } from "hooks/use-audit";
import { useSettingsStore } from "hooks/use-settings-store";
import { useTable } from "hooks/use-table";
import { useVulnerabilityStore } from "hooks/use-vulnerability-store";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

const DEBOUNCE_DELAY_MS = 1500;

const VulnerabilitySeverityUpdateRequests: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();
  const { pathname } = useLocation();
  const { vulnerabilityId } = useParams() as { vulnerabilityId?: string };
  const [search, setSearch] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { cvssVersion } = useSettingsStore();
  const { isApprovingSeverityUpdate, setIsApprovingSeverityUpdate } =
    useVulnerabilityStore();

  const navigate = useNavigate();
  const formRef = useModal("handle-severity-request-form");

  const {
    data: vulnsData,
    refetch: meVulnsRefetch,
    loading: vulnsLoading,
    fetchMore,
  } = useQuery(GET_VULNERABILITIES_SEVERITY_UPDATE_REQUESTS, {
    fetchPolicy: "no-cache",
    onCompleted: (): void => {
      addAuditEvent("ToDo.SeverityUpdateRequests", "unknown");
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.warning(
          "An error occurred fetching severity update requests",
          error,
        );
      });
    },
  });

  const vulnerabilities = useMemo(
    (): IVulnRowAttr[] =>
      _.isUndefined(vulnsData)
        ? []
        : vulnsData.me.vulnerabilitiesSeverityRequests.edges.map(
            (edge): IVulnRowAttr => edge?.node as IVulnRowAttr,
          ),
    [vulnsData],
  );

  const pageInfo = _.isUndefined(vulnsData)
    ? { endCursor: "", hasNextPage: false }
    : vulnsData.me.vulnerabilitiesSeverityRequests.pageInfo;

  const handleNextPage = useCallback(async (): Promise<void> => {
    if (pageInfo.hasNextPage) {
      await fetchMore({ variables: { after: pageInfo.endCursor } });
    }
  }, [pageInfo.hasNextPage, pageInfo.endCursor, fetchMore]);

  const closeConfirmModal = useCallback((): void => {
    setIsConfirmModalOpen(false);
    setIsApprovingSeverityUpdate(!isApprovingSeverityUpdate);
  }, [setIsApprovingSeverityUpdate, isApprovingSeverityUpdate]);

  const refetchVulns = useCallback(async (): Promise<void> => {
    await meVulnsRefetch();
  }, [meVulnsRefetch]);

  const onRowClick = useCallback(
    (row: Row<IVulnRowAttr>): void => {
      navigate(`${pathname}/${row.original.id}`, { replace: true });
    },
    [navigate, pathname],
  );

  const formattedVulns = useMemo(
    (): IVulnRowAttr[] =>
      vulnerabilities.filter(
        ({ hacker, groupName, proposedSeverityAuthor, where }): boolean =>
          where.toLowerCase().includes(search.toLowerCase()) ||
          groupName.toLowerCase().includes(search.toLowerCase()) ||
          (hacker?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
          (proposedSeverityAuthor
            ?.toLowerCase()
            .includes(search.toLowerCase()) ??
            false),
      ),
    [vulnerabilities, search],
  );

  const { table } = useTable({
    columns: getColumns({
      accessorKey: cvssVersion.scoreKey,
      header: cvssVersion.header,
    }),
    data: formattedVulns,
    fetchMore: handleNextPage,
    id: "vulns-severity-requests-table",
    options: {
      actionsOptions: { onSearch: setSearch },
      columnOptions: {
        hidden: ["finding.organizationName", "reportDate"],
        order: [
          "where",
          "specific",
          "groupName",
          "state",
          "severity",
          "proposedSeverity",
          "hacker",
          "proposedSeverityAuthor",
        ],
        pinned: ["where"],
      },
      filtersMode: "client-side",
      loading: vulnsLoading,
      paginationMode: "client-side",
      rowOptions: { enableMultiSelection: true, onClick: onRowClick },
    },
    total: formattedVulns.length,
  });

  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const selectedVulns: IVulnRowAttr[] = Object.keys(rowSelection).map(
    (key): IVulnRowAttr => tableData?.get(key) ?? ({} as IVulnRowAttr),
  );

  const clearSelected = useCallback((): void => {
    table.setRowSelection({});
  }, [table]);

  const onApprove = useCallback((): void => {
    if (isApprovingSeverityUpdate) {
      setIsApprovingSeverityUpdate(!isApprovingSeverityUpdate);
    } else {
      if (selectedVulns.length > 0) {
        setIsConfirmModalOpen(true);
      }
      setIsApprovingSeverityUpdate(!isApprovingSeverityUpdate);
    }
  }, [isApprovingSeverityUpdate, selectedVulns, setIsApprovingSeverityUpdate]);

  return (
    <React.StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Table.Columns table={table} />
            <ActionButtons
              areVulnsSelected={selectedVulns.length > 0}
              onApprove={onApprove}
              refreshVulns={refetchVulns}
            />
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
      {vulnerabilityId !== undefined && vulnerabilityId !== "" && (
        <VulnerabilityModal
          cvssVersion={cvssVersion.scoreKey}
          findingId={selectedVulns[0]?.findingId ?? ""}
          groupName={selectedVulns[0]?.groupName ?? ""}
          vulnerabilityId={vulnerabilityId}
        />
      )}
      <HandleSeverityRequestsForm
        clearSelected={clearSelected}
        findingId={selectedVulns[0]?.findingId ?? ""}
        modalRef={{ ...formRef, isOpen: isConfirmModalOpen }}
        onCancel={closeConfirmModal}
        refetchData={_.debounce(refetchVulns, DEBOUNCE_DELAY_MS)}
        vulnerabilities={selectedVulns}
      />
    </React.StrictMode>
  );
};

export { VulnerabilitySeverityUpdateRequests };
